/**
 * Bat Cave Service
 *
 * Service layer for the secure document transfer portal.
 * Self-service mode: External LOs can upload and select recipients themselves.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  BatCavePackage,
  BatCaveDocument,
  BatCaveRecipient,
  BatCaveDownload,
  CreateBatCaveLinkRequest,
  CreateBatCaveLinkResponse,
  ValidateBatCaveTokenResponse,
  UploadBatCaveDocumentResponse,
  CompleteBatCaveUploadResponse,
  DownloadBatCavePackageResponse,
  BatCaveUserOption
} from '@/types/batcave';

const log = createComponentLogger('BatCaveService', 'batcave');

/**
 * Get recipient options and company info for self-service portal (public)
 */
export async function getPortalData(): Promise<{
  recipients: Array<{ id: string; name: string; title?: string }>;
  company: { name: string; email?: string; logoUrl?: string; primaryColor?: string };
}> {
  log.info('Fetching portal data');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-batcave-recipients`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  const data = await response.json();

  if (!response.ok) {
    log.error('Failed to fetch portal data', { error: data.error });
    throw new Error(data.error || 'Failed to load portal');
  }

  return data;
}

/**
 * Create a new upload link
 */
export async function createUploadLink(
  request: CreateBatCaveLinkRequest
): Promise<CreateBatCaveLinkResponse> {
  log.info('Creating upload link', { name: request.name, recipientCount: request.recipientUserIds.length });

  const session = await supabase.auth.getSession();
  if (!session.data.session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-batcave-link`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session.access_token}`
      },
      body: JSON.stringify(request)
    }
  );

  const data = await response.json();

  if (!response.ok) {
    log.error('Failed to create upload link', { error: data.error });
    throw new Error(data.error || 'Failed to create upload link');
  }

  log.info('Upload link created', { packageId: data.packageId, uploadUrl: data.uploadUrl });
  return data;
}

/**
 * Validate an upload token (public, no auth required)
 */
export async function validateToken(
  token: string
): Promise<ValidateBatCaveTokenResponse> {
  log.info('Validating token');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-batcave-token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    }
  );

  const data = await response.json();

  if (!response.ok) {
    log.error('Token validation failed', { error: data.error });
    throw new Error(data.error || 'Invalid upload link');
  }

  log.info('Token validated', { packageId: data.packageId, name: data.name });
  return data;
}

/**
 * Upload a document (public, self-service mode)
 * If no packageId provided, creates a new package on first upload.
 * Returns packageId which should be used for subsequent uploads.
 */
export async function uploadDocument(
  file: File,
  packageId?: string,
  onProgress?: (progress: number) => void
): Promise<UploadBatCaveDocumentResponse & { packageId: string }> {
  log.info('Uploading document', { fileName: file.name, fileSize: file.size, packageId });

  const formData = new FormData();
  formData.append('file', file);
  if (packageId) {
    formData.append('packageId', packageId);
  }

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          log.info('Document uploaded', { documentId: data.documentId, packageId: data.packageId });
          resolve(data);
        } else {
          log.error('Upload failed', { error: data.error });
          reject(new Error(data.error || 'Upload failed'));
        }
      } catch {
        reject(new Error('Invalid response from server'));
      }
    });

    xhr.addEventListener('error', () => {
      log.error('Upload network error');
      reject(new Error('Network error during upload'));
    });

    xhr.open('POST', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-batcave-document`);
    xhr.send(formData);
  });
}

/**
 * Submit package with uploader info and recipients (public, self-service)
 */
export async function submitPackage(
  packageId: string,
  uploaderInfo: { name: string; email: string; phone: string },
  recipientUserIds: string[]
): Promise<CompleteBatCaveUploadResponse> {
  log.info('Submitting package', { packageId, recipientCount: recipientUserIds.length });

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-batcave-package`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        packageId,
        uploaderName: uploaderInfo.name,
        uploaderEmail: uploaderInfo.email,
        uploaderPhone: uploaderInfo.phone,
        recipientUserIds
      })
    }
  );

  const data = await response.json();

  if (!response.ok) {
    log.error('Submit package failed', { error: data.error });
    throw new Error(data.error || 'Failed to submit package');
  }

  log.info('Package submitted', { notificationsSent: data.notificationsSent });
  return data;
}

/**
 * Download a document or all documents from a package
 */
export async function downloadPackage(
  packageId: string,
  documentId?: string
): Promise<DownloadBatCavePackageResponse> {
  log.info('Downloading', { packageId, documentId });

  const session = await supabase.auth.getSession();
  if (!session.data.session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-batcave-package`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.data.session.access_token}`
      },
      body: JSON.stringify({ packageId, documentId })
    }
  );

  const data = await response.json();

  if (!response.ok) {
    log.error('Download failed', { error: data.error });
    throw new Error(data.error || 'Failed to get download');
  }

  return data;
}

/**
 * Get all packages (for admin view)
 */
export async function getPackages(): Promise<BatCavePackage[]> {
  log.info('Fetching packages');

  const { data, error } = await supabase
    .from('batcave_packages')
    .select(`
      *,
      recipients:batcave_recipients(
        *,
        user:users(id, first_name, last_name, email, title)
      )
    `)
    .order('created_at', { ascending: false });

  if (error) {
    log.error('Failed to fetch packages', { error });
    throw error;
  }

  return data || [];
}

/**
 * Get a single package with documents
 */
export async function getPackage(packageId: string): Promise<BatCavePackage | null> {
  log.info('Fetching package', { packageId });

  const { data, error } = await supabase
    .from('batcave_packages')
    .select(`
      *,
      recipients:batcave_recipients(
        *,
        user:users(id, first_name, last_name, email, title)
      ),
      documents:batcave_documents(*)
    `)
    .eq('id', packageId)
    .single();

  if (error) {
    log.error('Failed to fetch package', { error, packageId });
    throw error;
  }

  return data;
}

/**
 * Get documents for a package
 */
export async function getPackageDocuments(packageId: string): Promise<BatCaveDocument[]> {
  log.info('Fetching package documents', { packageId });

  const { data, error } = await supabase
    .from('batcave_documents')
    .select('*')
    .eq('package_id', packageId)
    .order('uploaded_at', { ascending: false });

  if (error) {
    log.error('Failed to fetch documents', { error, packageId });
    throw error;
  }

  return data || [];
}

/**
 * Get download history for a package
 */
export async function getPackageDownloads(packageId: string): Promise<BatCaveDownload[]> {
  log.info('Fetching package downloads', { packageId });

  const { data, error } = await supabase
    .from('batcave_downloads')
    .select(`
      *,
      user:users(first_name, last_name, email)
    `)
    .eq('package_id', packageId)
    .order('downloaded_at', { ascending: false });

  if (error) {
    log.error('Failed to fetch downloads', { error, packageId });
    throw error;
  }

  return data || [];
}

/**
 * Get non-loan-officer users for recipient selection
 */
export async function getNonLoanOfficerUsers(): Promise<BatCaveUserOption[]> {
  log.info('Fetching non-loan-officer users');

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
throw new Error('Not authenticated');
}

  // Get current user's organization
  const { data: currentUser } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (!currentUser?.organization_id) {
    throw new Error('User organization not found');
  }

  const { data, error } = await supabase
    .from('users')
    .select('id, first_name, last_name, email, title')
    .eq('organization_id', currentUser.organization_id)
    .eq('employment_status', 'active')
    .eq('is_loan_officer', false)
    .order('last_name');

  if (error) {
    log.error('Failed to fetch users', { error });
    throw error;
  }

  return (data || []).map(u => ({
    id: u.id,
    firstName: u.first_name,
    lastName: u.last_name,
    email: u.email,
    title: u.title
  }));
}

/**
 * Cancel a package
 */
export async function cancelPackage(packageId: string): Promise<void> {
  log.info('Cancelling package', { packageId });

  const { error } = await supabase
    .from('batcave_packages')
    .update({ status: 'cancelled' })
    .eq('id', packageId);

  if (error) {
    log.error('Failed to cancel package', { error, packageId });
    throw error;
  }
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
return '0 B';
}
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Get status badge color
 */
export function getStatusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'active':
      return 'bg-blue-100 text-blue-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'expired':
      return 'bg-gray-100 text-gray-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
